import type * as React from 'react';
import { Box, IconButton, ListSubheader, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { MinusIcon, PlusIcon } from '@phosphor-icons/react';

import { FONT_PACKS } from '@/lib/site-styles/font-packs';
import { resolveButtonFontKey, SITE_FONT_OPTIONS } from '@/styles/theme/create-unit-site-theme';
import type { FontClassification } from '@/styles/theme/font-catalog';
import type { ButtonFontSetting, SiteFontFamily, SiteThemeFonts } from '@/types/database';
import { PreviewCard } from './PreviewCard';
import { TwoColumn } from './TwoColumn';
import type { SiteStylesScreenProps } from './types';

const MIN_SIZE = 12;
const MAX_SIZE = 24;

// Same grouping Scouting Ops Hub's Website > Style > Fonts uses — a System
// Sans and a Display Serif behave very differently at body-text sizes, so
// grouping by classification (not just an alphabetical flat list) helps a
// unit pick with that in mind.
const FONT_CLASSIFICATION_ORDER: FontClassification[] = [
  'System Sans',
  'Humanist Sans',
  'Geometric Sans',
  'Grotesque Sans',
  'Traditional Serif',
  'Display Serif',
];

function groupedFontMenuItems(): React.JSX.Element[] {
  const items: React.JSX.Element[] = [];
  for (const classification of FONT_CLASSIFICATION_ORDER) {
    const options = SITE_FONT_OPTIONS.filter((option) => option.classification === classification);
    if (options.length === 0) continue;
    items.push(<ListSubheader key={`group-${classification}`}>{classification}</ListSubheader>);
    for (const option of options) {
      items.push(
        <MenuItem key={option.value} value={option.value}>
          <Stack>
            <Typography variant="body2">{option.label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {option.legibilityNote}
            </Typography>
          </Stack>
        </MenuItem>
      );
    }
  }
  return items;
}

const BUTTON_FONT_OPTIONS: { value: ButtonFontSetting; label: string }[] = [
  { value: 'inherit-heading', label: 'Match heading font' },
  { value: 'inherit-body', label: 'Match paragraph font' },
];

export function FontsScreen({ draft, updateTheme }: SiteStylesScreenProps) {
  const { fonts } = draft.theme_config;

  const setFonts = (patch: Partial<SiteThemeFonts>, options?: { immediate?: boolean }) =>
    updateTheme({ ...draft.theme_config, fonts: { ...fonts, ...patch } }, options);

  return (
    <Stack spacing={2.5} sx={{ p: 2.5 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
        Fonts
      </Typography>

      <TwoColumn
        controls={
          <>
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                All Font Packs
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                {FONT_PACKS.map((pack) => (
                  <Box
                    key={pack.id}
                    component="button"
                    type="button"
                    onClick={() => setFonts(pack.fonts, { immediate: true })}
                    sx={{
                      flexShrink: 0,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 999,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: 'transparent',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: 'text.primary',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {pack.name}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography>Base Size</Typography>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <IconButton
                  size="small"
                  disabled={fonts.baseSize <= MIN_SIZE}
                  onClick={() => setFonts({ baseSize: fonts.baseSize - 1 }, { immediate: true })}
                  aria-label="Decrease base size"
                >
                  <MinusIcon size={16} />
                </IconButton>
                <Typography sx={{ minWidth: 48, textAlign: 'center' }}>{fonts.baseSize} px</Typography>
                <IconButton
                  size="small"
                  disabled={fonts.baseSize >= MAX_SIZE}
                  onClick={() => setFonts({ baseSize: fonts.baseSize + 1 }, { immediate: true })}
                  aria-label="Increase base size"
                >
                  <PlusIcon size={16} />
                </IconButton>
              </Stack>
            </Stack>

            <FontRoleSelect label="Headings" value={fonts.headingFont} onChange={(value) => setFonts({ headingFont: value }, { immediate: true })} />
            <FontRoleSelect label="Paragraphs" value={fonts.paragraphFont} onChange={(value) => setFonts({ paragraphFont: value }, { immediate: true })} />
            <ButtonFontSelect
              value={fonts.buttonFont}
              resolvedLabel={SITE_FONT_OPTIONS.find((option) => option.value === resolveButtonFontKey(fonts))?.label}
              onChange={(value) => setFonts({ buttonFont: value }, { immediate: true })}
            />
            <FontRoleSelect label="Miscellaneous" value={fonts.miscFont} onChange={(value) => setFonts({ miscFont: value }, { immediate: true })} />
          </>
        }
        preview={
          <PreviewCard themeConfig={draft.theme_config}>
            <Stack spacing={1}>
              <Typography variant="h4">Heading</Typography>
              <Typography variant="h6" color="text.secondary">
                Subheading
              </Typography>
              <Typography>This is a paragraph of body text, set at your base size, so you can judge how it reads.</Typography>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', pt: 1 }}>
                <Box
                  component="button"
                  type="button"
                  sx={(theme) => ({
                    border: 'none',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: 1,
                    px: 2,
                    py: 0.75,
                    fontFamily: theme.typography.button.fontFamily,
                    fontSize: 14,
                    cursor: 'default',
                  })}
                >
                  Button
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Caption text
                </Typography>
              </Stack>
            </Stack>
          </PreviewCard>
        }
      />
    </Stack>
  );
}

function FontRoleSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: SiteFontFamily;
  onChange: (value: SiteFontFamily) => void;
}) {
  const selected = SITE_FONT_OPTIONS.find((option) => option.value === value);

  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value as SiteFontFamily)}
      fullWidth
      size="small"
      helperText={selected ? `${selected.classification} — ${selected.legibilityNote}` : undefined}
      slotProps={{ select: { MenuProps: { slotProps: { paper: { sx: { maxHeight: 360 } } } } } }}
    >
      {groupedFontMenuItems()}
    </TextField>
  );
}

// Buttons default to inheriting a font rather than picking a third,
// independent typeface — the design-system review flagged a fully
// independent button font as unnecessary stylistic risk on top of the
// heading/paragraph pair a unit has already chosen.
function ButtonFontSelect({
  value,
  resolvedLabel,
  onChange,
}: {
  value: ButtonFontSetting;
  resolvedLabel: string | undefined;
  onChange: (value: ButtonFontSetting) => void;
}) {
  return (
    <TextField
      select
      label="Buttons"
      value={value}
      onChange={(event) => onChange(event.target.value as ButtonFontSetting)}
      fullWidth
      size="small"
      helperText={resolvedLabel ? `Renders as ${resolvedLabel}` : undefined}
      slotProps={{ select: { MenuProps: { slotProps: { paper: { sx: { maxHeight: 360 } } } } } }}
    >
      {BUTTON_FONT_OPTIONS.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
      {groupedFontMenuItems()}
    </TextField>
  );
}
